import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  LockOutlined,
  PersonAddOutlined,
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  Google,
  Microsoft,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { ROUTES } from "../routes";
import { AuthAPI } from "../api/auth";

const PAGE_WIDTH = 1160;

function getDefaultItamBase(apps = []) {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      return (process.env.REACT_APP_ITAM_URL || 'http://localhost:3001').replace(/\/+$/, '');
    }
  }
  const appList = Array.isArray(apps) ? apps : [];
  const appUrl = appList.find((app) => app?.key === 'itam')?.url;
  if (appUrl) return appUrl;
  const envBase = process.env.REACT_APP_ITAM_URL ? process.env.REACT_APP_ITAM_URL.replace(/\/+$/, '') : '';
  if (envBase) return envBase;
  return 'https://itam.sushaservices.com';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { login, user } = auth;
  const returnTo = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("returnTo");
    return value && value.startsWith("/") ? value : null;
  }, [location.search]);

  React.useEffect(() => {
    if (user) {
      if (user.must_change_password) {
        navigate(ROUTES.CHANGE_PASSWORD, { replace: true });
        return;
      }
      if (user.role === 'partner') {
        navigate(ROUTES.PARTNER, { replace: true });
        return;
      }
      navigate(returnTo || ROUTES.WORKSPACE, { replace: true });
    }
  }, [navigate, returnTo, user]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });

  const openSnack = (msg, sev = "info") => setSnack({ open: true, msg, sev });

  const getLoginErrorMessage = (error) => {
    const rawMessage = error?.response?.data?.message || error?.response?.data?.error || "";
    const lower = String(rawMessage).toLowerCase();

    if (error?.response?.status === 401) {
      if (
        lower.includes("email") &&
        (lower.includes("incorrect") || lower.includes("not found") || lower.includes("does not exist"))
      ) {
        return "Email address not found. Please check and try again.";
      }
      if (lower.includes("password") || lower.includes("pass")) {
        return "Incorrect password. Please try again.";
      }
      return "Invalid email or password. Please try again.";
    }

    if (error?.response?.status === 403 && lower.includes("verify")) {
      return rawMessage || "Please verify your email before signing in.";
    }

    return rawMessage || "Login failed. Please try again.";
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!email) return openSnack("Email is required", "error");
    if (!password) return openSnack("Password is required", "error");

    setLoading(true);
    try {
      const session = await login(email.trim(), password);
      if (remember) localStorage.setItem("remember_email", email.trim());
      if (session?.must_change_password) {
        navigate(ROUTES.CHANGE_PASSWORD, { replace: true });
        return;
      }
      navigate(user?.role === 'partner' ? ROUTES.PARTNER : (returnTo || ROUTES.WORKSPACE), { replace: true });
    } catch (error) {
      if (error?.response?.status === 403 && error?.response?.data?.emailVerified === false) {
        localStorage.setItem("unverified_email", error.response.data.email || email.trim());
        navigate(ROUTES.VERIFY_EMAIL, { replace: true });
        return;
      }
      openSnack(getLoginErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const onForgotClick = async (e) => {
    e.preventDefault();
    if (!email) return openSnack("Please enter your email first", "warning");
    try {
      await AuthAPI.requestReset(email.trim());
      openSnack("If the email exists, we sent a reset link.", "success");
    } catch {
      openSnack("If the email exists, we sent a reset link.", "success");
    }
  };

  React.useEffect(() => {
    const rem = localStorage.getItem("remember_email");
    if (rem) setEmail(rem);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: 2,
        py: { xs: 3, sm: 4, md: 6 },
        bgcolor: "#F8FAFC",
        backgroundImage:
          "radial-gradient(circle at 0% 0%, rgba(37,99,235,0.10) 0%, rgba(37,99,235,0) 38%), radial-gradient(circle at 100% 100%, rgba(14,116,144,0.08) 0%, rgba(14,116,144,0) 36%)",
      }}
    >
      <Container maxWidth={false} disableGutters sx={{ width: "100%", maxWidth: PAGE_WIDTH, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid #DBE6F3",
            bgcolor: "#FFFFFF",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
              minHeight: { md: 620 },
            }}
          >
            <Box
              sx={{
                p: { xs: 2.5, sm: 3, md: 4.2 },
                bgcolor: "#f0f7ff",
                borderRight: { md: "1px solid #DBE6F3" },
                backgroundImage:
                  "linear-gradient(160deg, rgba(37,99,235,0.09) 0%, rgba(255,255,255,0) 45%)",
              }}
            >
              <Chip
                label="ITAM"
                sx={{
                  mb: 1.8,
                  bgcolor: "#DBEAFE",
                  color: "#1D4ED8",
                  fontWeight: 800,
                  letterSpacing: 0.4,
                }}
              />
              <Chip
                label="Helpdesk"
                size="small"
                variant="outlined"
                sx={{
                  ml: 1,
                  mb: 1.8,
                  bgcolor: "#EFF6FF",
                  borderColor: "#BFDBFE",
                  color: "#1D4ED8",
                  fontWeight: 800,
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1.2,
                  fontSize: { xs: "1.45rem", sm: "1.8rem", md: "2.15rem" },
                }}
              >
                Automate Your Assets Management and Support
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#475569",
                  mt: 1.6,
                  mb: 2.3,
                  maxWidth: 520,
                }}
              >
                Track, maintain and manage laptops, servers, and software licenses from one workspace.
              </Typography>

              <Stack spacing={1.4} sx={{ mb: 2.3 }}>
                {[
                  "Save 25+ hours per month with automated tracking",
                  "Reduce IT costs by identifying unused assets",
                  "Manage helpdesk requests and support work in one place",
                  "Never miss warranty, AMC, or license renewals",
                ].map((point) => (
                  <Box key={point} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleOutline sx={{ fontSize: 20, color: "#0ea5e9" }} />
                    <Typography variant="body2" sx={{ color: "#0f172a", fontWeight: 600 }}>
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: "#BFDBFE",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Typography variant="caption" sx={{ color: "#334155", fontWeight: 700 }}>
                  Need a quick walkthrough?
                </Typography>
                <Typography variant="body2" sx={{ color: "#334155", mt: 0.4 }}>
                  Explore the sandbox from the top navigation or book a guided demo on the website.
                </Typography>
              </Paper>
            </Box>

            <Box
              component="form"
              onSubmit={onSubmit}
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: "#EFF6FF",
                    color: "#1D4ED8",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <LockOutlined fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Welcome back
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sign in to access your enterprise workspace
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1.2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Google />}
                  onClick={() => {
                    const target = returnTo || ROUTES.WORKSPACE;
                    window.location.assign(AuthAPI.googleStartUrl(target));
                  }}
                  sx={{ py: 1.1, textTransform: "none", fontWeight: 700 }}
                >
                  Continue with Google Workspace
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Microsoft />}
                  onClick={() => {
                    const target = returnTo || ROUTES.WORKSPACE;
                    window.location.assign(AuthAPI.m365StartUrl(target));
                  }}
                  sx={{ py: 1.1, textTransform: "none", fontWeight: 700 }}
                >
                  Continue with Microsoft 365
                </Button>
              </Stack>

              <Box sx={{ my: 2.2 }}>
                <Divider>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    or
                  </Typography>
                </Divider>
              </Box>

              <TextField
                label="Email Address"
                type="email"
                fullWidth
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                margin="normal"
                inputProps={{ "aria-label": "Email Address" }}
              />

              <TextField
                label="Password"
                type={showPw ? "text" : "password"}
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                margin="normal"
                inputProps={{ "aria-label": "Password" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={showPw ? "Hide password" : "Show password"}
                        onClick={() => setShowPw((s) => !s)}
                      >
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  mt: 0.5,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <FormControlLabel
                  control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} size="small" />}
                  label="Remember me"
                />
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={onForgotClick}
                  sx={{ border: "none", background: "none", cursor: "pointer", p: 0 }}
                >
                  Forgot password?
                </MuiLink>
              </Box>

              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                disabled={loading}
                sx={{ py: 1.25, textTransform: "none", fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Sign In"}
              </Button>

              <Box sx={{ mt: 2.6 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.4 }}>
                  New to Susha Services?
                </Typography>
              </Box>
              <Button
                component={Link}
                to={ROUTES.REGISTER}
                fullWidth
                variant="outlined"
                startIcon={<PersonAddOutlined />}
                sx={{ py: 1.2, textTransform: "none", fontWeight: 600 }}
              >
                Create Account
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Need help?{" "}
            <MuiLink href="mailto:support@sushaservices.com" sx={{ fontWeight: 600 }}>
              Contact Support
            </MuiLink>
          </Typography>
        </Box>

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev} variant="filled">
            {snack.msg}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
